import type { Dict } from "@/lib/i18n";

/**
 * Availability-timeline dictionary (AvailabilityCard / AvailabilityRibbon)
 * — owned by the AVAILABILITY cluster. en + it mandatory; de/es provided
 * for the full set (small surface, and the card ships on the BESS pages
 * the nav core already covers in all four languages).
 *
 * Italian follows the sibling EMS Dashboard's operator vocabulary
 * (TB-EMS messages.ts): "Nessun dato", "finestra", "Contatore",
 * "Impossibile caricare …", "Riprova".
 */
export const DICT_AVAILABILITY: Dict = {
  "availability.title":  { en: "Availability", it: "Disponibilità", de: "Verfügbarkeit", es: "Disponibilidad" },
  "availability.uptime": { en: "Uptime",       it: "Uptime",        de: "Betriebszeit",  es: "Tiempo activo" },

  "availability.gridMeter": { en: "Grid meter", it: "Contatore di rete", de: "Netzzähler", es: "Medidor de red" },

  "availability.explainer": {
    en: "Recorded telemetry coverage per unit — striped segments are windows with no data; click one to inspect it in the Historical analyzer.",
    it: "Copertura della telemetria registrata per unità — i segmenti rigati sono finestre senza dati; cliccane uno per ispezionarlo nello storico.",
    de: "Abdeckung der aufgezeichneten Telemetrie pro Einheit — gestreifte Segmente sind Fenster ohne Daten; zum Prüfen im Verlauf anklicken.",
    es: "Cobertura de la telemetría registrada por unidad — los segmentos rayados son ventanas sin datos; haz clic en uno para inspeccionarlo en el Histórico.",
  },

  // Tooltip/aria on a clickable gap segment. {from}/{to} are clock times,
  // {duration} is a pre-formatted duration ("42 min", "1 h 20 min").
  "availability.gapTitle": {
    en: "No data {from}–{to} ({duration}) — click to inspect",
    it: "Nessun dato {from}–{to} ({duration}) — clicca per ispezionare",
    de: "Keine Daten {from}–{to} ({duration}) — zum Prüfen klicken",
    es: "Sin datos {from}–{to} ({duration}) — haz clic para inspeccionar",
  },

  // Header-row summary next to the uptime %. {duration} pre-formatted.
  "availability.gapTotal": {
    en: "{duration} without data",
    it: "{duration} senza dati",
    de: "{duration} ohne Daten",
    es: "{duration} sin datos",
  },

  "availability.loadError": {
    en: "Couldn't load history for this row",
    it: "Impossibile caricare lo storico per questa riga",
    de: "Verlauf für diese Zeile konnte nicht geladen werden",
    es: "No se pudo cargar el histórico de esta fila",
  },

  "availability.empty.title": {
    en: "No units to monitor",
    it: "Nessuna unità da monitorare",
    de: "Keine Einheiten zu überwachen",
    es: "No hay unidades que supervisar",
  },
  "availability.empty.detail": {
    en: "This site's topology has no BESS units and no grid meter, so there is nothing to draw an availability timeline for.",
    it: "La topologia di questo sito non ha unità BESS né un contatore di rete, quindi non c'è nulla per cui tracciare una cronologia di disponibilità.",
    de: "Die Topologie dieser Anlage hat weder BESS-Einheiten noch einen Netzzähler — es gibt nichts, wofür eine Verfügbarkeits-Zeitleiste gezeichnet werden könnte.",
    es: "La topología de este sitio no tiene unidades BESS ni medidor de red, así que no hay nada para lo que trazar una cronología de disponibilidad.",
  },

  // aria-label on the ribbon track. {name} is the unit label.
  "availability.ribbonAria": {
    en: "Availability timeline for {name}",
    it: "Cronologia di disponibilità per {name}",
    de: "Verfügbarkeits-Zeitleiste für {name}",
    es: "Cronología de disponibilidad de {name}",
  },

  // aria-label on the 24h/7d/30d selector group.
  "availability.windowAria": {
    en: "Availability window",
    it: "Finestra di disponibilità",
    de: "Verfügbarkeitsfenster",
    es: "Ventana de disponibilidad",
  },
};
